import type { NameLocalizations } from '../../api/localization';
import type { AppLanguage } from './centralLocalization';

export function personNameLocalizations(record: unknown, originalField = 'full_name'): NameLocalizations | undefined {
  if (!record || typeof record !== 'object') return undefined;
  const row = record as Record<string, unknown>;
  const primaryName = ['full_name', 'fullName', 'name'].includes(originalField);
  const value = row[`${originalField}_localizations`] ?? (primaryName ? row.name_localizations ?? row.nameLocalizations : undefined);
  if (!value || typeof value !== 'object') return undefined;
  const name = value as NameLocalizations;
  return name.original === row[originalField] && name.ar && typeof name.ar.value === 'string'
    && name.en && typeof name.en.value === 'string' ? name : undefined;
}

/** Display only: callers keep the original source/ID in their state and APIs. */
export function personName(record: unknown, language: AppLanguage, originalField = 'full_name', official = false): string {
  if (!record || typeof record !== 'object') return typeof record === 'string' ? record : '';
  const original = (record as Record<string, unknown>)[originalField];
  const variants = personNameLocalizations(record, originalField);
  const chosen = variants?.[language];
  const valid = chosen && ['original', 'reviewed', 'manually_corrected'].includes(chosen.status)
    && chosen.value.trim() && (!official || chosen.source_original === original);
  return valid ? chosen.value : typeof original === 'string' ? original : '';
}

/** Both spellings are search aliases only; option values/selected IDs stay exact. */
export function personNameSearchText(record: unknown, originalField = 'full_name'): string {
  if (!record || typeof record !== 'object') return typeof record === 'string' ? record : '';
  const original = (record as Record<string, unknown>)[originalField];
  const variants = personNameLocalizations(record, originalField);
  return [original, ...(['ar', 'en'] as const).map(locale => {
    const variant = variants?.[locale];
    return variant && ['original', 'reviewed', 'manually_corrected'].includes(variant.status) ? variant.value : undefined;
  })].filter(value => typeof value === 'string').join(' ');
}
